import { useInfiniteQuery, useQuery } from 'react-query';
import { notificationsAxiosApi } from 'src/Config/axiosApi';
import {
  NotificationEventType,
  NotificationsFilter,
  NotificationsListResponse,
} from '../types/notifications.types';
import {
  NOTIFICATION_DISPLAY,
  getNotificationDirection,
} from '../components/notifications/notification-display';

function buildParams(
  filter: NotificationsFilter,
  cursor?: string,
): Record<string, any> {
  const out: Record<string, any> = {};
  if (cursor) out.cursor = cursor;
  if (filter.from) out.from = filter.from;
  if (filter.to) out.to = filter.to;
  if (filter.ruleId) out.ruleId = filter.ruleId;
  if (filter.pairId) out.pairId = filter.pairId;

  // `direction` is a derived attribute (a function of `type`), so the
  // backend doesn't know about it directly. Expand it to a concrete
  // `type[]` here so server-side pagination doesn't return rows that
  // the client would just throw away in `matchesFilter`. When the user
  // also picks specific types, intersect the two — picking
  // direction=entry AND type=maker_placed (an unwinder type) yields
  // the empty set, which the page renders as "no results" (correct).
  let effectiveTypes: NotificationEventType[] | undefined = filter.types;
  if (filter.direction && filter.direction !== 'all') {
    const allTypes = Object.keys(NOTIFICATION_DISPLAY) as NotificationEventType[];
    const directionMatches = allTypes.filter(
      (t) => getNotificationDirection(t) === filter.direction,
    );
    effectiveTypes =
      filter.types && filter.types.length > 0
        ? filter.types.filter((t) => directionMatches.includes(t))
        : directionMatches;
    // Empty intersection means the user picked direction=entry AND a
    // type list with no entry_* members (or vice versa). Send a
    // sentinel that matches nothing instead of dropping the param —
    // dropping would silently fall back to "all types" and surface
    // the wrong rows.
    if (effectiveTypes.length === 0) {
      effectiveTypes = ['__no_matching_types__' as NotificationEventType];
    }
  }
  if (effectiveTypes && effectiveTypes.length > 0) {
    // Repeated `type` query params; axios + Express both accept the
    // array shape and the controller normalizes single vs array.
    out.type = effectiveTypes;
  }
  if (filter.platform && filter.platform !== 'all') {
    out.platform = filter.platform;
  }
  if (filter.source && filter.source !== 'all') {
    out.source = filter.source;
  }
  if (filter.severity && filter.severity !== 'all') {
    out.severity = filter.severity;
  }
  return out;
}

export function useNotifications(
  filter: NotificationsFilter,
  opts?: { enabled?: boolean },
) {
  return useInfiniteQuery<NotificationsListResponse>(
    ['notifications', filter],
    async ({ pageParam }) => {
      const params = buildParams(filter, pageParam as string | undefined);
      const { data } = await notificationsAxiosApi.get<NotificationsListResponse>(
        '/notifications',
        { params },
      );
      return data;
    },
    {
      enabled: opts?.enabled !== false,
      getNextPageParam: (last) => last.nextCursor ?? undefined,
      // Refetch on focus is too noisy with the live WS overlay; we
      // hold the page steady once loaded, the WS keeps it fresh.
      refetchOnWindowFocus: false,
      staleTime: 5_000,
    },
  );
}

export function useNotificationTypes() {
  return useQuery<{ types: string[] }>(
    ['notifications', 'types'],
    async () => {
      const { data } = await notificationsAxiosApi.get<{ types: string[] }>(
        '/notifications/types',
      );
      return data;
    },
    { staleTime: 60 * 60 * 1000 },
  );
}
